import React from 'react';

import { ParsedData, StepProps, track } from '@betterment/js-runtime';
import { Text } from '@betterment/samba-react';
import { Button, CenteredWorkflowLayout, FormikField, Heading } from '@betterment/style-closet-react';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { RequestValues } from './Controller';

import { JointInvitationRequestParams, JointInvitationRequestResponse } from '../Checking/data';
import { createFieldValidator } from '../Checking/form-helpers';
import FlashErrorMessage from '../Shared/components/FlashErrorMessage';

export interface Props extends StepProps<RequestValues> {
  jointInvitationRequest: (data: JointInvitationRequestParams) => Promise<ParsedData<JointInvitationRequestResponse>>;
  searchResult: JointInvitationRequestResponse | null;
}

const EmailSchema = Yup.string().required("Email can't be blank").email('Enter a valid email');

const SsnSchema = Yup.string()
  .required("Social security number can't be blank")
  .matches(/^[0-9]{3}-?[0-9]{2}-?[0-9]{4}$/, 'Enter a valid social security number');

const JointInvitationRequestsUserLookup: React.FC<React.PropsWithChildren<Props>> = ({
  flowState,
  goToNextStep,
  jointInvitationRequest,
  searchResult,
}) => {
  const isLimitReached = searchResult?.searchStatus === 'limit_reached';

  const onContinue = async (
    { socialSecurityNumber, email }: RequestValues,
    { setFieldError, setFieldTouched }: FormikHelpers<RequestValues>
  ) => {
    const { data, errors } = await jointInvitationRequest({ socialSecurityNumber, email });

    track('ElementClicked', { name: 'ContinueToJCUserConfirmation', flow: 'OpenJointCheckingPrimary' });
    void setFieldTouched('base', true, false);

    if (errors) {
      setFieldError('base', 'Something went wrong. Please try again.');
    } else if (data?.searchStatus === 'not_found') {
      track('ElementViewed', { name: 'CantVerifyUser', flow: 'OpenJointCheckingPrimary' });
      setFieldError('base', 'We are having trouble verifying the identity of the secondary user.');
    } else if (data?.searchStatus === 'limit_reached') {
      track('ElementViewed', { name: 'ExceededAttempts', flow: 'OpenJointCheckingPrimary' });
      setFieldError('base', 'You have exceeded the number of allowed attempts.');
    } else if (data?.searchStatus === 'invited_self') {
      track('ElementViewed', { name: 'CantInviteSelf', flow: 'OpenJointCheckingPrimary' });
      setFieldError('base', 'You cannot open a Joint Checking account with yourself');
    } else if (data?.searchStatus === 'found') {
      goToNextStep({ invitedUser: data.searchResult, requestId: data.requestId });
    } else if (data?.searchStatus === 'prospect_secondary') {
      goToNextStep({
        email,
        socialSecurityNumber,
        isProspectSecondary: true,
      });
    }
  };

  return (
    <CenteredWorkflowLayout title="Joint Checking signup">
      <CenteredWorkflowLayout.Slot name="content">
        <Heading variant="primary">Who are you adding to your Joint Checking account?</Heading>
        <Text
          as="p"
          css={{
            marginBottom: 'space.300',
          }}
        >
          Their email and social security number will help us check if they already have a Betterment account. If they
          don't, we'll use these to help securely set up their account
        </Text>
        <Formik onSubmit={onContinue} initialValues={flowState.values} validateOnBlur={false} validateOnChange={false}>
          {({ isSubmitting, validateField }) => (
            <>
              <FlashErrorMessage name="base" />
              <Form>
                <FormikField
                  as="string"
                  name="email"
                  label="Email"
                  mb={[0, null, null, null]}
                  {...createFieldValidator<string>('email', EmailSchema, validateField)}
                />
                <FormikField
                  as="taxId"
                  name="socialSecurityNumber"
                  label="Social security number"
                  placeholder="xxx-xx-xxxx"
                  mb={[0, null, null, null]}
                  {...createFieldValidator<string>('socialSecurityNumber', SsnSchema, validateField)}
                />
                <CenteredWorkflowLayout.Actions>
                  <Button type="submit" disabled={isSubmitting || isLimitReached}>
                    Continue
                  </Button>
                </CenteredWorkflowLayout.Actions>
              </Form>
            </>
          )}
        </Formik>
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};

export default JointInvitationRequestsUserLookup;
